import React, { useMemo } from "react";
import useApi from "../useApi";
import { getApiPaths } from "../constants/healthEndpoints";
import { openEntityWithOneDayDuration } from "../buildServiceItems";
import Scrollable from "../shared/Scrollable";

/**
 * StoresAttention Component
 * Shows stores requiring attention for a specific market
 */
const StoresAttention = ({ accountId }) => {
  const { STORES_VIEW } = getApiPaths(accountId);
  
  const {
    data: storesData = [],
    loading: storesLoading,
    error: storesError,
  } = useApi(STORES_VIEW);

  // Filter and sort stores with issues
  const storesWithIssues = useMemo(() => {
    return (storesData || [])
      .filter((store) => {
        const hasIssue = 
          store?.status === "down" || 
          store?.color === "red" || 
          store?.color === "amber";
        return hasIssue;
      })
      .sort((a, b) => {
        // Sort by severity: red > amber > others
        if (a.color === "red" && b.color !== "red") return -1;
        if (a.color !== "red" && b.color === "red") return 1;
        if (a.color === "amber" && b.color !== "amber") return -1;
        if (a.color !== "amber" && b.color === "amber") return 1;
        return Number(a.store) - Number(b.store);
      })
      .slice(0, 10); // Top 10 stores
  }, [storesData]);

  const handleStoreClick = (store) => {
    const entityGuid = store?.guid || "MzU5MjU1NnxWSVp8REFTSEJPQVJEfGRhOjEyMjEyOTc5";
    openEntityWithOneDayDuration(entityGuid, store.store);
  };

  if (storesLoading) {
    return (
      <div className="global-card">
        <div className="global-card-header">
          <h2>Top Stores Requiring Attention</h2>
        </div>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          Loading stores...
        </div>
      </div>
    );
  }

  if (storesError) {
    return (
      <div className="global-card">
        <div className="global-card-header">
          <h2>Top Stores Requiring Attention</h2>
        </div>
        <div style={{ padding: "2rem", color: "#ef4444" }}>
          Error loading stores: {storesError.message}
        </div>
      </div>
    );
  }

  return (
    <div className="global-card">
      <div className="global-card-header">
        <h2>Top Stores Requiring Attention</h2>
      </div>

      {storesWithIssues.length === 0 ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#68b561" }}>
          ✓ No stores with issues
        </div>
      ) : (
        /* Ten stores overflowed the card and stretched the whole row. It
           scrolls inside the card now, with the header pinned so the columns
           stay readable on the way down. */
        <Scrollable className="global-card-scroll">
        <table className="global-table global-table--sticky">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Store</th>
              <th>Status</th>
              <th>Issue</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {storesWithIssues.map((store, index) => (
              <tr key={`${store.store}-${index}`}>
                <td>{index + 1}</td>
                <td>
                  <strong>{store.store}</strong>
                </td>
                <td>
                  <span
                    className={`status-badge ${
                      store.color === "red"
                        ? "status-critical"
                        : store.color === "amber"
                        ? "status-warning"
                        : "status-healthy"
                    }`}
                  >
                    {store.status || "Unknown"}
                  </span>
                </td>
                <td>
                  <span className="store-detail">
                    {store.detail || "No details"}
                  </span>
                </td>
                <td>
                  <button
                    className="store-action-button"
                    onClick={() => handleStoreClick(store)}
                    title={`View store ${store.store} details`}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </Scrollable>
      )}
    </div>
  );
};

export default StoresAttention;